import { useState, useRef, useCallback } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ACCEPTED_UPLOAD_MIME,
  partitionUploadFiles,
} from '@/lib/uploadConstraints'

interface UploadZoneProps {
  onUpload: (files: File[]) => void
  /** Surfaced to the parent when the user drops something we won't ship. */
  onRejected?: (reason: string) => void
  disabled?: boolean
  /**
   * Compact variant collapses the visual to a single horizontal bar — used
   * when the page is dense (post-first-upload Overview). Default is the
   * tall hero variant for the empty state.
   */
  compact?: boolean
  /**
   * MIME allowlist override. When provided, the hidden file input's `accept`
   * attribute is set to this string instead of the global default.
   */
  acceptedMime?: string
}

/**
 * Drag-drop / click-to-pick upload tile. Pure UI — file processing happens
 * in the parent (OverviewPage hands files to documentService.upload).
 *
 * The drag-over state is a pure CSS hover, so screen readers don't get a
 * chatty "now hovering" event stream. Keyboard users hit `Enter` on the
 * button, which programmatically clicks the hidden input.
 */
export function UploadZone({
  onUpload,
  onRejected,
  disabled,
  compact,
  acceptedMime,
}: UploadZoneProps) {
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFiles = useCallback(
    (fileList: FileList | null | undefined) => {
      if (!fileList || fileList.length === 0) return
      const { accepted, rejected } = partitionUploadFiles(
        Array.from(fileList)
      )
      if (rejected.length > 0) {
        onRejected?.(rejected[0].reason)
      }
      if (accepted.length > 0) {
        onUpload(accepted)
      }
    },
    [onUpload, onRejected]
  )

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled) setDragActive(true)
  }
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
  }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    if (disabled) return
    handleFiles(e.dataTransfer.files)
  }

  const openPicker = () => {
    if (disabled) return
    inputRef.current?.click()
  }

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="upload-zone"
      className={cn(
        'relative rounded-lg border border-dashed border-border-default bg-bg-primary',
        'transition-colors duration-150',
        dragActive && 'border-primary bg-primary/4',
        disabled && 'opacity-60 pointer-events-none',
        compact ? 'px-4 py-3' : 'px-6 py-10'
      )}
    >
      <input
        ref={inputRef}
        title='Your startup idea description'
        type="file"
        multiple
        accept={acceptedMime ?? ACCEPTED_UPLOAD_MIME}
        onChange={(e) => {
          handleFiles(e.target.files)
          // Reset so re-picking the same file fires onChange again.
          e.target.value = ''
        }}
        className="sr-only"
        aria-hidden
      />
      <button
        type="button"
        onClick={openPicker}
        className={cn(
          'w-full flex items-center gap-3 text-left',
          compact ? 'flex-row' : 'flex-col items-center text-center gap-4'
        )}
        aria-label="Add documents to your context engine"
      >
        <span
          className={cn(
            'flex items-center justify-center rounded-md bg-primary/10 text-primary shrink-0',
            compact ? 'w-9 h-9' : 'w-12 h-12'
          )}
        >
          <Upload size={compact ? 16 : 20} />
        </span>
        <span className="flex-1">
          <span
            className={cn(
              'block font-display font-semibold text-text-primary',
              compact ? 'text-sm' : 'text-lg'
            )}
          >
            Drop files to add context
          </span>
          <span
            className={cn(
              'block text-text-secondary',
              compact ? 'text-xs' : 'text-sm mt-1'
            )}
          >
            PDFs, slides, audio recordings, text files… anything about your
            business.
          </span>
        </span>
      </button>
    </div>
  )
}
